import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, FormHelperText } from '@mui/material';
import { useUserContext } from '../contexts/UserContext';
import { useCardContext } from '../contexts/CardContext';

export default function GetStarted({ onContinue }) {
  const { userState, setUserName } = useUserContext();
  const { cardId, setCardFullName } = useCardContext();
  const { userName } = userState;

  // Local state to manage the input value
  const [inputValue, setInputValue] = useState(userName);
  const [error, setError] = useState('');

  // Effect to synchronize the input value with context when userName changes
  useEffect(() => {
    setInputValue(userName);
  }, [userName]);

  // Handle user name change and update both contexts
  const handleUserNameChange = (event) => {
    const newName = event.target.value;
    setInputValue(newName); // Update local state first

    // Validate that the name is not just numbers
    if (/^[a-zA-Z\s]*$/.test(newName) || newName === '') {
      setError(''); // Clear error message if valid
    } else {
      setError('Invalid name. Please enter alphabetic characters only.'); // Set error message
    }
  };

  // Handle blur event to update context when user finishes editing
  const handleBlur = () => {
    if (!error) {
      setUserName(inputValue);
      setCardFullName(cardId, inputValue); // Update card name with cardId
    }
  };

  // Handle key down event
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default form submission if in a form
      if (inputValue && !error) { // Check if inputValue is set and no error
        // Update contexts before continuing
        setUserName(inputValue);
        setCardFullName(cardId, inputValue);
        onContinue(); // Call the onContinue function
      }
    }
  };

  // Handle continue button click
  const handleContinue = () => {
    if (inputValue && !error) {
      // Update contexts before continuing
      setUserName(inputValue);
      setCardFullName(cardId, inputValue);
      console.log('🚀 GetStarted: Continuing to next step with name:', inputValue);
      onContinue();
    }
  };

  // Check if the userName is valid for enabling the button
  const isUserNameValid = /^[a-zA-Z\s]*$/.test(inputValue) && inputValue.trim().length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 3,
      }}
    >
      <Typography variant="h5" gutterBottom>
        Let's get started
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 2 }}>
        Create your Touch digital business card in 3 simple steps.
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: 3 }}>
        First, let's start with your name.
      </Typography>

      <TextField
        label="Name"
        value={inputValue} // Bind the TextField to local state
        onChange={handleUserNameChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur} // Update context on blur
        variant="outlined"
        fullWidth
        autoFocus
        sx={{ marginBottom: 1 }} // Adjusted margin for better spacing
        error={!!error} // Set error prop to true if there's an error
        placeholder="Enter your full name"
      />

      {/* Display error message */}
      {error && <FormHelperText error>{error}</FormHelperText>}

      <Button
        variant="contained"
        color="primary"
        sx={{
          backgroundColor: isUserNameValid ? 'blue' : 'grey',
          borderRadius: '4px',
          textTransform: 'none',
          marginTop: 2
        }}
        disabled={!isUserNameValid}
        onClick={handleContinue}
      >
        Continue
      </Button>
    </Box>
  );
}